import { Link } from "react-router-dom";
import { Store, ShoppingCart, Salad, CreditCard, Check, ArrowRight } from "lucide-react";

const MicroMarkets = () => {
  const features = [
    "Open shelving display",
    "Fresh food options daily",
    "Self-checkout kiosk",
    "Premium product selection",
    "Refrigerated sections",
    "Customizable layout",
  ];

  return (
    <div className="min-h-screen" data-testid="micro-markets-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-navy relative overflow-hidden" data-testid="micro-hero">
        <div className="absolute top-10 left-10 w-8 h-8 bg-gold rotate-12 opacity-60" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        <div className="absolute bottom-20 right-20 w-10 h-10 bg-coral opacity-40" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-gold text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
                MICRO MARKETS
              </span>
              <h1 className="font-heading text-4xl md:text-5xl text-cream mb-4">
                A Mini <span className="text-coral">Convenience Store</span> Experience
              </h1>
              <p className="text-cream/80 text-lg mb-8">
                Step beyond vending with our micro market solutions. Open
                shelving, fresh food, and a self-checkout experience that feels
                like a premium café.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="micro-cta"
              >
                Explore Micro Markets <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="bg-coral/20 rounded-2xl p-8 border-2 border-cream/20">
                <Store className="h-32 w-32 text-cream mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's a Micro Market */}
      <section className="py-20 md:py-32" data-testid="what-is-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
                WHAT IS IT?
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-navy mb-6">
                More Than Vending
              </h2>
              <p className="text-navy/70 mb-6">
                A micro market transforms your break room into a self-service
                convenience store. Unlike traditional vending, products are
                displayed on open shelves—visible, accessible, and fresh.
              </p>
              <p className="text-navy/70 mb-6">
                Users browse freely, grab what they want, and pay at a sleek
                self-checkout kiosk. It's the elevated refreshment experience
                your space deserves.
              </p>
              <div className="bg-gold/30 rounded-xl p-6 border-2 border-navy">
                <p className="font-bold text-navy mb-2">Ideal for:</p>
                <ul className="text-navy/70 text-sm space-y-1">
                  <li>• Offices with 50+ employees</li>
                  <li>• Large apartment communities</li>
                  <li>• Healthcare facilities</li>
                  <li>• Universities and schools</li>
                </ul>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border-2 border-navy p-6 shadow-[4px_4px_0px_0px_#F2CC8F]">
                <div className="text-center">
                  <div className="font-heading text-4xl text-coral mb-2">Unlimited</div>
                  <p className="text-navy/70">Product variety</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl border-2 border-navy p-6 shadow-[4px_4px_0px_0px_#E07A5F]">
                <div className="text-center">
                  <div className="font-heading text-4xl text-coral mb-2">24/7</div>
                  <p className="text-navy/70">Access for your team</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl border-2 border-navy p-6 shadow-[4px_4px_0px_0px_#81B29A]">
                <div className="text-center">
                  <div className="font-heading text-4xl text-coral mb-2">$0</div>
                  <p className="text-navy/70">Cost to your property</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 md:py-32 bg-gold/20" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">
              What's in a Micro Market?
            </h2>
            <p className="text-navy/70 max-w-2xl mx-auto">
              Fresh food, snacks, drinks, and more—all displayed beautifully and
              ready to grab.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Fresh & Prepared */}
            <div className="card-retro" data-testid="category-fresh">
              <div className="bg-coral w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <Salad className="h-7 w-7 text-cream" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Fresh & Prepared</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Salads
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Sandwiches
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Wraps
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Fresh Fruit
                </li>
              </ul>
            </div>

            {/* Grab & Go */}
            <div className="card-retro" data-testid="category-grab">
              <div className="bg-coral w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <Store className="h-7 w-7 text-cream" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Grab & Go</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Yogurt Parfaits
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Hummus Packs
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Cheese & Crackers
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Hard-Boiled Eggs
                </li>
              </ul>
            </div>

            {/* Pantry Staples */}
            <div className="card-retro" data-testid="category-pantry">
              <div className="bg-coral w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <ShoppingCart className="h-7 w-7 text-cream" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Pantry Staples</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Snacks
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Drinks
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Candy
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Gum & Mints
                </li>
              </ul>
            </div>

            {/* Easy Checkout */}
            <div className="card-retro" data-testid="category-checkout">
              <div className="bg-coral w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <CreditCard className="h-7 w-7 text-cream" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Easy Checkout</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Self-Service Kiosk
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Tap to Pay
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Employee Accounts
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full" />Mobile Payment
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-4">
              FEATURES
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-navy">
              The Premium Experience
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border-2 border-navy p-6 flex items-center gap-4 shadow-[4px_4px_0px_0px_#1A2A3A]"
              >
                <div className="bg-matcha w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="h-5 w-5 text-cream" />
                </div>
                <span className="text-navy font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-coral" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-cream mb-4">
            Ready to Upgrade Your Space?
          </h2>
          <p className="text-cream/90 mb-8 max-w-xl mx-auto">
            Bring the convenience store experience to your property at zero cost.
          </p>
          <Link
            to="/contact"
            className="btn-secondary inline-block"
            data-testid="cta-button"
          >
            Request a Micro Market
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MicroMarkets;
